import { useCallback, useEffect } from 'react';

import type { LoadedDiffDoc } from './context';
import { useReviewsSelection } from './context';

export type KnowledgeReviewDiffLoaderProps = {
  /** Called when selectedItem changes. Return diff payload or null. */
  fetchDiffForItem: (itemId: string) => Promise<LoadedDiffDoc | null>;
};

/**
 * Listens to selectedItem from context and fetches diff via the provided callback.
 * Owns loading state and context updates; host (Storybook or Platform) supplies fetch logic.
 */
export function KnowledgeReviewDiffLoader({ fetchDiffForItem }: KnowledgeReviewDiffLoaderProps) {
  const { selectedItem, setLoadedDiffDoc, setLoadingDiff } = useReviewsSelection();

  const loadDiffForItem = useCallback(
    async (itemId: string, signal: AbortSignal) => {
      setLoadingDiff(true);
      setLoadedDiffDoc(null);

      try {
        const doc = await fetchDiffForItem(itemId);
        if (signal.aborted) return;
        setLoadedDiffDoc(doc);
      } catch (error) {
        if (signal.aborted) return;
        console.error('Failed to fetch diff:', error);
        setLoadedDiffDoc(null);
      } finally {
        if (!signal.aborted) {
          setLoadingDiff(false);
        }
      }
    },
    [fetchDiffForItem, setLoadedDiffDoc, setLoadingDiff],
  );

  const selectedId = selectedItem?.id;

  useEffect(() => {
    if (!selectedId) {
      setLoadedDiffDoc(null);
      setLoadingDiff(false);

      return;
    }

    const abortController = new AbortController();
    void loadDiffForItem(selectedId, abortController.signal);

    return () => {
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, loadDiffForItem]);

  return null;
}
